import React, { useState } from 'react'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import { Add as AddIcon } from '@material-ui/icons'

import { useQueryPaginator } from 'src/components/Table'
import { ASNListTable } from 'src/components/Table/list/ASN'
import { BarcodedUnitListTable } from 'src/components/Table/list/BarcodedUnit'
import { BulkSkuUploadDialog } from 'src/components/Dialog'
import { BulkOrderUploadDialog } from 'src/modules/supply_chain/components/freight/BulkOrderUploadDialog'
import { BulkASNUploadDialog } from 'src/modules/supply_chain/components/BulkASNUploadDialog'
import { ClientDetailTable } from 'src/components/Table/detail/Client'
import { ClientDialog } from 'src/modules/supply_chain/components/ClientDialog'
import { ClientUserDialog } from 'src/modules/supply_chain/components/ClientUserDialog'
import { ClientUserListTable } from 'src/components/Table/list/ClientUser'
import { DamagedListTable } from 'src/components/Table/list/Damaged'
import { CartListTable } from 'src/components/Table/list/Cart'
import { EventListTable } from 'src/components/Table/list/Event'
import { FreightOrderListTable } from 'src/components/Table/list/FreightOrder'
import { FreightShipmentBarcodedUnitListTable } from 'src/components/Table/list/FreightShipmentBarcodedUnit'
import { FreightShipmentListTable } from 'src/components/Table/list/FreightShipment'
import { FreightShipmentUnitListTable } from 'src/components/Table/list/FreightShipmentUnit'
import { PalletListTable } from 'src/components/Table/list/Pallet'
import { ParcelOrderListTable } from 'src/components/Table/list/ParcelOrder'
import { ParcelShipmentBarcodedUnitListTable } from 'src/components/Table/list/ParcelShipmentBarcodedUnit'
import { ParcelShipmentListTable } from 'src/components/Table/list/ParcelShipment'
import { ParcelShipmentUnitListTable } from 'src/components/Table/list/ParcelShipmentUnit'
import { SkuListTable } from 'src/components/Table/list/Sku'
import { UnitListTable } from 'src/components/Table/list/Unit'

import { skuit } from 'src/services/skuit'
import { useQuery } from 'react-query'

import { DetailPage } from 'src/components/Pages/DetailPage'
import { DetailTab } from 'src/components/Pages/Tab'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

interface PathProps {
  guid: string
}

const InventoryTab = () => {
  const { guid } = useParams<PathProps>()
  const [csvUploadDialogOpen, setCsvUploadDialogOpen] = useState(false)

  const skusPaginator = useQueryPaginator('sku-list', skuit.sc.skus.list, {
    params: { page_size: 5, clients: guid },
  })
  const palletsPaginator = useQueryPaginator('pallet-list', skuit.sc.pallets.list, {
    params: { page_size: 5, clients: guid },
  })
  const unitsPaginator = useQueryPaginator('unit-list', skuit.sc.units.list, {
    params: { page_size: 5, clients: guid },
  })
  const damagedPaginator = useQueryPaginator('damaged-list', skuit.sc.damaged.list, {
    params: { page_size: 5, clients: guid },
  })
  const barcodedPaginator = useQueryPaginator('barcoded-unit-list', skuit.sc.barcodedUnits.list, {
    params: { page_size: 5, clients: guid },
  })

  return (
    <DetailTab>
      {csvUploadDialogOpen && (
        <BulkSkuUploadDialog
          clientGuid={guid}
          itemNameToUpload="SKUs"
          open={csvUploadDialogOpen}
          handleClose={() => setCsvUploadDialogOpen(false)}
          onSuccess={() => {
            skusPaginator.refresh()
          }}
        />
      )}
      <SkuListTable
        paginator={skusPaginator}
        fields={['sku', 'product', 'numAvailable', 'numReserved']}
        extraActions={[
          {
            title: '+ SKUs',
            onClick: () => {
              setCsvUploadDialogOpen(true)
            },
          },
        ]}
      />
      <PalletListTable
        paginator={palletsPaginator}
        fields={['name', 'client', 'clientID', 'skus', 'path', 'totalUnits', 'batch', 'asn', 'reviewNeeded']}
        bulkActions={['printPallet']}
      />
      <UnitListTable
        paginator={unitsPaginator}
        fields={['path', 'sku', 'numUnits', 'dateReceived']}
      />
      <DamagedListTable paginator={damagedPaginator} fields={['guid', 'product', 'sku', 'path']} />
      <BarcodedUnitListTable paginator={barcodedPaginator} fields={['guid', 'product', 'path']} />
    </DetailTab>
  )
}

const InboundTab = () => {
  const { guid } = useParams<PathProps>()
  const [bulkASNOpen, setBulkASNOpen] = useState(false)
  const asnPaginator = useQueryPaginator('asn-list', skuit.sc.asn.list, {
    params: { page_size: 5, clients: guid },
  })

  return (
    <DetailTab>
      {bulkASNOpen && (
        <BulkASNUploadDialog
          open={bulkASNOpen}
          handleClose={() => setBulkASNOpen(false)}
          clientGuid={guid}
        />
      )}
      <ASNListTable
        paginator={asnPaginator}
        fields={['trackingNumber', 'consignee', 'facility', 'carrier', 'status']}
        extraActions={[{ title: 'Bulk Add ASN', icon: <AddIcon />, onClick: () => setBulkASNOpen(true) }]}
      />
    </DetailTab>
  )
}

const FreightTab = () => {
  const { guid } = useParams<PathProps>()
  const [bulkOrderOpen, setBulkOrderOpen] = useState(false)
  const freightOrdersPaginator = useQueryPaginator(
    'freight-order-list',
    skuit.sc.freight.orders.list,
    {
      params: { page_size: 5, clients: guid },
    }
  )
  const freightShipmentSkusPaginator = useQueryPaginator(
    'freight-shipment-unit-list',
    skuit.sc.freight.shipments.units.list,
    { params: { page_size: 5, clients: guid, statuses: 'pending' } }
  )
  const freightShipmentUnitsPaginator = useQueryPaginator(
    'freight-shipment-barcoded-unit-list',
    skuit.sc.freight.shipments.barcodedUnits.list,
    { params: { page_size: 5, clients: guid } }
  )
  const freightShipmentsPaginator = useQueryPaginator(
    'freight-shipment-list',
    skuit.sc.freight.shipments.list,
    {
      params: { page_size: 5, clients: guid },
    }
  )

  return (
    <DetailTab>
      {bulkOrderOpen && (
        <BulkOrderUploadDialog
          open={bulkOrderOpen}
          handleClose={() => setBulkOrderOpen(false)}
          clientGuid={guid}
        />
      )}
      <FreightOrderListTable
        paginator={freightOrdersPaginator}
        fields={['number', 'placedOn', 'fulfillOn', 'status']}
        extraActions={[{ title: 'Bulk Add Orders', icon: <AddIcon />, onClick: () => setBulkOrderOpen(true) }]}
      />
      <FreightShipmentListTable
        paginator={freightShipmentsPaginator}
        fields={['trackingNumber', 'consignee', 'facility', 'carrier', 'status']}
      />
      <FreightShipmentUnitListTable
        paginator={freightShipmentSkusPaginator}
        title="SKUs in Active Freight Shipments"
        fields={['shipment', 'sku', 'numUnits']}
      />
      <FreightShipmentBarcodedUnitListTable
        paginator={freightShipmentUnitsPaginator}
        title="Units (Barcoded) in Active Freight Shipments"
        fields={['shipment', 'unit', 'product']}
      />
    </DetailTab>
  )
}

const ParcelTab = () => {
  const { guid } = useParams<PathProps>()
  const parcelOrdersPaginator = useQueryPaginator(
    'parcel-order-list',
    skuit.sc.parcel.orders.list,
    {
      params: { page_size: 5, clients: guid },
    }
  )
  const parcelShipmentsPaginator = useQueryPaginator(
    'parcel-shipment-list',
    skuit.sc.parcel.shipments.list,
    {
      params: { page_size: 5, clients: guid },
    }
  )
  const parcelShipmentSkusPaginator = useQueryPaginator(
    'parcel-shipment-unit-list',
    skuit.sc.parcel.shipments.units.list,
    { params: { page_size: 5, clients: guid, statuses: 'pending' } }
  )
  const parcelShipmentUnitsPaginator = useQueryPaginator(
    'parcel-shipment-barcoded-unit-list',
    skuit.sc.parcel.shipments.barcodedUnits.list,
    { params: { page_size: 5, clients: guid } }
  )

  return (
    <DetailTab>
      <ParcelShipmentUnitListTable
        paginator={parcelShipmentSkusPaginator}
        title="Units in Active Parcel Shipments"
        fields={['shipment', 'sku', 'numUnits']}
      />
      <ParcelShipmentBarcodedUnitListTable
        paginator={parcelShipmentUnitsPaginator}
        title="Units (Barcoded) in Active Parcel Shipments"
        fields={['shipment', 'unit', 'product']}
      />
      <ParcelOrderListTable
        paginator={parcelOrdersPaginator}
        fields={['number', 'placedOn', 'fulfillOn', 'status']}
      />
      <ParcelShipmentListTable
        paginator={parcelShipmentsPaginator}
        fields={['trackingNumber', 'consignee', 'facility', 'carrier', 'status']}
      />
    </DetailTab>
  )
}

const CartsTab = () => {
  const { guid } = useParams<PathProps>()
  const eventsPaginator = useQueryPaginator('cart-list', skuit.sc.carts.list, {
    params: { page_size: 30, clients: guid },
  })

  return (
    <DetailTab>
      <CartListTable
        paginator={eventsPaginator}
        fields={['name', 'autoSync']}
        rowActions={['sync']}
      />
    </DetailTab>
  )
}

const EventsTab = () => {
  const { guid } = useParams<PathProps>()
  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  return (
    <DetailTab>
      <EventListTable
        paginator={eventsPaginator}
        title="Client Events"
        fields={['type', 'data', 'by', 'on']}
      />
    </DetailTab>
  )
}

const UsersTab = () => {
  const { guid } = useParams<PathProps>()
  const [createOpen, setCreateOpen] = useState(false)
  const userPaginator = useQueryPaginator(
    'client-user-list',
    (config) => skuit.sc.clients.users.list(guid, config),
    {
      params: { page_size: 20 },
    }
  )

  return (
    <DetailTab>
      {createOpen && (
        <ClientUserDialog
          open={createOpen}
          handleClose={() => {
            setCreateOpen(false)
          }}
          onSave={() => userPaginator.refresh()}
          clientGuid={guid}
        />
      )}
      <ClientUserListTable
        paginator={userPaginator}
        title="Client Events"
        fields={['email', 'firstName', 'lastName', 'isActive']}
        extraActions={[
          { title: 'Add User', icon: <AddIcon />, onClick: () => setCreateOpen(true) },
        ]}
      />
    </DetailTab>
  )
}

export const ClientPage = () => {
  const { guid } = useParams<PathProps>()
  const [createOpen, setCreateOpen] = useState(false)

  const client = useQuery(['client-details', guid], () => skuit.sc.clients.retrieve(guid))

  const refresh = () => {
    client.refetch()
  }

  return (
    <>
      {createOpen && (
        <ClientDialog
          open={createOpen}
          handleClose={() => {
            setCreateOpen(false)
          }}
        />
      )}
      <DetailPage
        obj={client}
        title="Client"
        refresh={refresh}
        breadcrumbs={[
          { title: 'Dashboard', url: '/' },
          { title: 'Clients', url: '/clients' },
        ]}
        detailComponent={<ClientDetailTable obj={client} refresh={refresh} />}
        menuItems={[
          { Icon: AddIcon, actions: [{ title: 'New Client', onClick: () => setCreateOpen(true) }] },
        ]}
        tabs={['Inventory', 'Inbound', 'Freight', 'Parcel', 'Events', 'Users', 'Carts']}
      >
        <InventoryTab />
        <InboundTab />
        <FreightTab />
        <ParcelTab />
        <EventsTab />
        <UsersTab />
        <CartsTab />
      </DetailPage>
    </>
  )
}
